
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { i18nManager } from "../i18n";
import PageHead from "./common/PageHead";
import SectionHead from "./common/SectionHead";
import ManagerCard from "./common/ManagerCard";
import ManagerModal from "./common/ManagerModal";
import { Utils } from "../../../../utils";
import styles from "../assets/style.module.css";
import { i18nManager as globalI18nManager } from "../../../../i18n";
import { BaseService, IHostManager, IHostWebsite, OmneHostService, SanitizedResponse } from "../../../../services";

export interface AboutProps {
    website: IHostWebsite;
}

function About({ website }: AboutProps) {

    const labels = i18nManager.Labels.about;
    const location = ReactRouterDom.useLocation();
    const settings = website.about_settings ?? {} as IHostWebsite["about_settings"];
    const [managers, setManagers] = React.useState<IHostManager[]>();
    const [selected, setSelected] = React.useState<IHostManager | null>(null);
    const addresses = (website.physical_addresses ?? []).filter((a) => a.street || a.city || a.state || a.country);
    const emails = (website.emails_and_phones ?? []).map((e) => e.email).filter(Boolean);
    const phones = (website.emails_and_phones ?? []).map((e) => e.phone).filter(Boolean);
    const contacts: [string, string, React.ReactNode[]][] = [
        ...((settings.show_physical_address && addresses.length) ? [["fa fa-location-dot", labels.address, addresses.map((a, i) => (<span key={i}>
            {[a.street, a.city, a.state, a.postal_code, a.country ? globalI18nManager.queryCountryCurrency(a.country, "country_code", "country_name") : undefined].filter(Boolean).join(", ")}
        </span>))] as [string, string, React.ReactNode[]]] : []),
        ...((settings.show_contact_emails_and_phones && emails.length) ? [["fa fa-envelope", labels.emails, emails.map((e) => (<a key={e} href={`mailto:${e}`}>{e}</a>))] as [string, string, React.ReactNode[]]] : []),
        ...((settings.show_contact_emails_and_phones && phones.length) ? [["fa fa-phone", labels.phones, phones.map((p) => (<a key={p} href={`tel:${p}`}>{p}</a>))] as [string, string, React.ReactNode[]]] : []),
    ];

    React.useEffect(() => {
        OmneHostService.getInstance().queryHostManagers(website.host.external_id, {
            size: 100,
            sort: "role,DESC",
        }).then(({ content }: SanitizedResponse<IHostManager>) => {
            setManagers((content ?? []).filter((m) => !m.settings?.hide_from_managers_page));
        }).catch(BaseService.reportError);
    }, [website.host.external_id]);

    React.useEffect(() => {
        if (location.hash === "#team" && managers?.length) document.getElementById("team")?.scrollIntoView({ behavior: "smooth" });
    }, [location.hash, managers]);

    return (<>
        <PageHead tag={labels.kicker} title={labels.title.replace(":name", website.host.name)} subtitle={website.host.short_info || labels.desc} />

        {settings.info && (<section className={styles["about"]}>
            <div className={styles["container"]}>
                <div className={styles["about-split"]}>
                    {website.host.logo_url && (<div className={styles["about-logo"]}><img src={website.host.logo_url} alt={website.host.name} /></div>)}
                    <div className={styles["article-body"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(settings.info) }} />
                </div>
            </div>
        </section>)}

        {contacts.length > 0 && (<section className={styles["section"]}>
            <div className={styles["container"]}>
                <SectionHead tag={labels.contact} title={labels.contact_title} />
                <div className={styles["contact-grid"]}>
                    {contacts.map(([icon, label, values], i) => (<div key={label} className={styles["contact-card"]}>
                        <span className={styles["tag"]}>[ {String(i + 1).padStart(2, "0")} ] {label}</span>
                        <i className={icon} />
                        <div className={styles["contact-values"]}>{values}</div>
                    </div>))}
                </div>
            </div>
        </section>)}

        {(settings.show_sponsors && !!website.sponsors?.length) && (<section className={styles["section"]}>
            <div className={styles["container"]}>
                <SectionHead tag={labels.partners} title={labels.partners_title} />
                <div className={styles["sponsor-grid"]}>
                    {website.sponsors.map((s) => (<div key={s} className={styles["sponsor"]}><img src={s} alt="" loading="lazy" /></div>))}
                </div>
            </div>
        </section>)}

        {(settings.show_people !== false && !!managers?.length) && (<section id="team" className={styles["section"]}>
            <div className={styles["container"]}>
                <SectionHead tag={labels.team} title={labels.team_title} />
                <div className={styles["crew-grid"]}>
                    {managers.map((m, i) => (<ManagerCard key={m.external_id} index={i} manager={m} onSelect={setSelected} />))}
                </div>
            </div>
        </section>)}

        {selected && (<ManagerModal manager={selected} onClose={() => setSelected(null)} />)}
    </>);
}

export default About;
